// Types
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useRouter } from 'next/router';

import { TranslationService } from '@/utils/services';

import useTranslation from '@/utils/hooks/translation/useTranslation';
//Hooks
import useCurrentSession from '@/utils/hooks/user/useCurrentSession';

import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';

// Libs
import { FORGOT_PASSWORD, SIGN_IN, SIGN_UP } from '@/lib/routes/publicRoutes';

//Components
import H1 from '@/components/base/Headings/H1';
import InfoBanner from '@/components/base/InfoBanner';
import LinkBase from '@/components/base/LinkBase';
import LoaderPage from '@/components/base/Loader/LoaderPage';
import LayoutSignIn from '@/components/layouts/LayoutSignIn';
import ForgotPasswordForm from '@/components/pages/forgotPassword/ForgotPasswordForm';

const ForgotPassword = () => {
    const { t: tCommons } = useTranslation('commons');
    const { t: tSignin } = useTranslation('signin');
    const { isLoading, user } = useCurrentSession({ isLoggedRedirect: true });
    const router = useRouter();
    const { deleteQuery } = useRouterQuery();
    const { invalid_user, token_expired } = router.query;

    const [errorMessage, setErrorMessage] = useState<ReactNode>(null);

    useEffect(() => {
        if (token_expired) {
            setErrorMessage(tSignin('reset_password_error.token_expired'));
            router.replace(deleteQuery('token_expired'));
        } else if (invalid_user) {
            setErrorMessage(
                tSignin.rich('reset_password_error.invalid_user', {
                    '0': (chunks) => (
                        <LinkBase href={SIGN_UP} className="underline">
                            {chunks}
                        </LinkBase>
                    )
                })
            );
            router.replace(deleteQuery('invalid_user'));
        }
    }, [invalid_user, token_expired]);

    if (isLoading || user) {
        return <LoaderPage />;
    }

    return (
        <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY}>
            <LayoutSignIn>
                {errorMessage === 'success' ? (
                    <InfoBanner
                        type="success"
                        title={tCommons('success')}
                        message={tSignin('forgot_password_banner_success')}
                        className="mb-12 max-w-md"
                        onClose={() => setErrorMessage(null)}
                    />
                ) : null}
                {errorMessage && errorMessage !== 'success' ? (
                    <InfoBanner
                        title={tCommons('error')}
                        message={errorMessage}
                        className="mb-12 max-w-md"
                        onClose={() => setErrorMessage(null)}
                    />
                ) : null}
                <H1 className="mb-m">
                    {tSignin.rich('forgot_your_password', {
                        br: () => <br />
                    })}
                </H1>
                <p className="mb-10 font-GMedium text-sm text-icoGray-500">
                    {tSignin('forget_password_notice')}
                </p>
                <ForgotPasswordForm setErrorMessage={setErrorMessage} />
                <p className="mt-10 text-center font-GSemiBold text-sm">
                    <LinkBase href={SIGN_IN}>{tSignin('go_back_to_log_in')}</LinkBase>
                </p>
            </LayoutSignIn>
        </GoogleReCaptchaProvider>
    );
};

export const getServerSideProps =
    TranslationService.getTranslationMessagesFromRoute(FORGOT_PASSWORD);

export default ForgotPassword;
